/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import {
  LanguageAndConfigPayload,
  useCreateLanguageAndConfig
} from '@utils/api/back-office/language-and-config/use-create-language-and-config.tsx';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset
} from 'react-hook-form';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Dispatch, SetStateAction } from 'react';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import toast from 'react-hot-toast';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context';

type Props = {
  control: Control<LanguageAndConfigPayload>;
  errors: FieldErrors<LanguageAndConfigPayload>;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  reset: UseFormReset<LanguageAndConfigPayload>;
  handleSubmit: UseFormHandleSubmit<
    LanguageAndConfigPayload,
    LanguageAndConfigPayload
  >;
  register: UseFormRegister<LanguageAndConfigPayload>;
};

export default function CreateLanguageAndConfig({
  control,
  errors,
  isModalOpen,
  setIsModalOpen,
  reset,
  handleSubmit,
  register
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { mutate: createLanguageAndConfig } = useCreateLanguageAndConfig();

  const onSubmitCreate = (data: LanguageAndConfigPayload) => {
    createLanguageAndConfig(data, {
      onSuccess: () => {
        toast.success(t('language-and-configuration-created-successfully'));
        setIsModalOpen(false);
        reset({
          id: undefined,
          bcp47: '',
          key: '',
          name: '',
          is_private: false,
          is_enabled: false,
          asr_url: '',
          asr_model_location: '',
          asr_audio_rate: undefined,
          punctuation_url: '',
          punctuation_model_location: '',
          asr_version: '',
          asr_common_version: '',
          alignment_language_code: '',
          alignment_version: '',
          deepl_code: ''
        });
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error?.response?.data.detail ||
          error?.response?.data.error ||
          error?.response?.data.message ||
          t('an-error-occurred-while-creating-the-language-and-configuration');

        toast.error(errorMessage);
      }
    });
  };

  const FORM_ID = 'create-language-config-form';

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      size="large"
      title={t('create-language-config')}
      footerActions={[
        <Button
          key="cancel"
          onClick={() => {
            setIsModalOpen(false);
            reset();
          }}
          label={t('cancel')}
          size="small"
          variant="smoke"
        />,
        <Button
          key="create"
          type="submit"
          form={FORM_ID}
          label={t('create')}
          size="small"
          variant="primary"
        />
      ]}
    >
      <div className="overflow-y-auto max-h-[70vh]">
        <form
          id={FORM_ID}
          onSubmit={handleSubmit(onSubmitCreate)}
          className="space-y-4"
        >
          {/* Basics Section */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <h3
              className="text-sm font-medium mb-1"
              style={{ color: getColour('normalText') }}
            >
              {t('basics')}
            </h3>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('basic-language-identifiers')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                id="name"
                label={t('name')}
                type="text"
                size="small"
                autoComplete="name"
                placeholder={t('name')}
                {...register('name')}
                error={errors.name?.message}
              />
              <Input
                id="key"
                label={t('key')}
                type="text"
                size="small"
                autoComplete="key"
                placeholder={t('key')}
                {...register('key')}
                error={errors.key?.message}
              />
              <Input
                id="bcp47"
                label="BCP-47"
                type="text"
                size="small"
                autoComplete="bcp47"
                placeholder="BCP-47"
                {...register('bcp47')}
                error={errors.bcp47?.message}
              />
            </div>
          </div>

          {/* ASR Settings Section */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <h3
              className="text-sm font-medium mb-1"
              style={{ color: getColour('normalText') }}
            >
              {t('asr-settings')}
            </h3>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('configure-asr-endpoints-and-versions')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                id="asr_url"
                label={t('asr-url')}
                type="text"
                size="small"
                autoComplete="asr_url"
                placeholder={t('asr-url')}
                {...register('asr_url')}
                error={errors.asr_url?.message}
              />
              <Input
                id="asr_model_location"
                label={t('model-location')}
                type="text"
                size="small"
                autoComplete="asr_model_location"
                placeholder={t('model-location')}
                {...register('asr_model_location')}
                error={errors.asr_model_location?.message}
              />
              <Input
                id="asr_audio_rate"
                label={t('sample-rate')}
                type="number"
                size="small"
                autoComplete="asr_audio_rate"
                placeholder={t('sample-rate')}
                {...register('asr_audio_rate')}
                error={errors.asr_audio_rate?.message}
              />
              <Input
                id="asr_version"
                label={t('version')}
                type="text"
                size="small"
                autoComplete="asr_version"
                placeholder={t('version')}
                {...register('asr_version')}
                error={errors.asr_version?.message}
              />
              <Input
                id="asr_common_version"
                label={t('common-version')}
                type="text"
                size="small"
                autoComplete="asr_common_version"
                placeholder={t('common-version')}
                {...register('asr_common_version')}
                error={errors.asr_common_version?.message}
              />
            </div>
          </div>

          {/* Punctuation Section */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <h3
              className="text-sm font-medium mb-1"
              style={{ color: getColour('normalText') }}
            >
              {t('punctuation')}
            </h3>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('punctuation-model-configuration')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                id="punctuation_url"
                label={t('punctuation-url')}
                type="text"
                size="small"
                autoComplete="punctuation_url"
                placeholder={t('punctuation-url')}
                {...register('punctuation_url')}
                error={errors.punctuation_url?.message}
              />
              <Input
                id="punctuation_model_location"
                label={t('punctuation-model-location')}
                type="text"
                size="small"
                autoComplete="punctuation_model_location"
                placeholder={t('punctuation-model-location')}
                {...register('punctuation_model_location')}
                error={errors.punctuation_model_location?.message}
              />
            </div>
          </div>

          {/* Alignment Section */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <h3
              className="text-sm font-medium mb-1"
              style={{ color: getColour('normalText') }}
            >
              {t('alignment')}
            </h3>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('alignment-language-and-version')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                id="alignment_language_code"
                label={t('language-code')}
                type="text"
                size="small"
                autoComplete="alignment_language_code"
                placeholder={t('language-code')}
                {...register('alignment_language_code')}
                error={errors.alignment_language_code?.message}
              />
              <Input
                id="alignment_version"
                label={t('version')}
                type="text"
                size="small"
                autoComplete="alignment_version"
                placeholder={t('version')}
                {...register('alignment_version')}
                error={errors.alignment_version?.message}
              />
            </div>
          </div>

          {/* Translation Section */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <h3
              className="text-sm font-medium mb-1"
              style={{ color: getColour('normalText') }}
            >
              {t('translation')}
            </h3>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('deepl-and-related-codes')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                id="deepl_code"
                label={t('deepl-code')}
                type="text"
                size="small"
                autoComplete="deepl_code"
                placeholder={t('deepl-code')}
                {...register('deepl_code')}
                error={errors.deepl_code?.message}
              />
            </div>
          </div>

          {/* Permissions Section */}
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <h3
              className="text-sm font-medium mb-1"
              style={{ color: getColour('normalText') }}
            >
              {t('permissions')}
            </h3>
            <p
              className="text-xs mb-3"
              style={{ color: getColour('inactiveText') }}
            >
              {t('control-language-visibility-and-status')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div
                className="p-3 rounded-2xl"
                style={{ backgroundColor: getColour('secondLayer') }}
              >
                <Controller
                  name="is_enabled"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center justify-between">
                      <div>
                        <p
                          className="text-xs font-medium"
                          style={{ color: getColour('normalText') }}
                        >
                          {t('language-enabled')}
                        </p>
                        <p
                          className="text-[10px] mt-0.5"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t('allow-this-language-to-be-used-for-processing')}
                        </p>
                      </div>
                      <Checkbox
                        id="is_enabled"
                        name={field.name}
                        ref={field.ref}
                        checked={Boolean(field.value)}
                        onChange={e => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        variant="primary"
                        className="h-5 w-5"
                      />
                    </div>
                  )}
                />
              </div>
              <div
                className="p-3 rounded-2xl"
                style={{ backgroundColor: getColour('secondLayer') }}
              >
                <Controller
                  name="is_private"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center justify-between">
                      <div>
                        <p
                          className="text-xs font-medium"
                          style={{ color: getColour('normalText') }}
                        >
                          {t('private-language')}
                        </p>
                        <p
                          className="text-[10px] mt-0.5"
                          style={{ color: getColour('inactiveText') }}
                        >
                          {t('restrict-visibility-to-specific-customers')}
                        </p>
                      </div>
                      <Checkbox
                        id="is_private"
                        name={field.name}
                        ref={field.ref}
                        checked={Boolean(field.value)}
                        onChange={e => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        variant="primary"
                        className="h-5 w-5"
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
